import { useMount } from '@tiger/hooks';
import lodash from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import HashLink from 'components/HashLink';
import classlist from 'utils/classlist';
import { FormatLetterItemType } from './type';

interface ViewWithNavProps {
    data: FormatLetterItemType[];
    value?: string;
    onChange?: (value: string) => void;
}

interface LetterParamType {
    letterItemHeight: number; // 每个菜单选项的高度
    letterRoundHeight: number; // 菜单取整的高度
    letterRectTop: number;
}

const ViewWithNav: FC<ViewWithNavProps> = ({ value, data, onChange }) => {
    const isTouchMoveRef = useRef<boolean>(false);
    const sectionRef = useRef<HTMLElement>(null);
    const countryListMapRef = useRef<Array<{ letter: string; territory: number }>>([]);
    const letterListRef = useRef<HTMLDivElement>(null);
    const optionItemRefs = useRef<Array<HTMLDivElement | null>>([]);
    const letterPosRef = useRef<Partial<LetterParamType>>({});

    const [curLetter, setCurLetter] = useState<string>(() =>
        value
            ? data.find(item => item.province.some(({ province }) => province === value))?.letter || data[0].letter
            : data[0].letter
    );

    // 监听滚动事件，调整被选中的国家
    const handleScroll = (event?: Event) => {
        if (event) {
            event.preventDefault();

            const target = event.target as HTMLElement;
            const scrollTop = target.scrollTop;
            // 计算滚动条是否小于某 item 底部临界线，并获得该 item 的 letter
            const letter = countryListMapRef.current.find(item => scrollTop < item.territory)?.letter;

            letter && setCurLetter(letter);
        }
    };

    useEffect(() => {
        if (curLetter && !isTouchMoveRef.current) {
            letterAutoPosition();
        }
    }, [curLetter]);

    useMount(() => {
        initMenuList();
        initCountryList();
        value && countryAutoPosition(value);

        const handleScrollDebounce = lodash.throttle(handleScroll, 100);

        sectionRef.current?.addEventListener('scroll', handleScrollDebounce);

        return () => {
            sectionRef.current?.removeEventListener('scroll', handleScrollDebounce);
        };
    });

    // 获取菜单对应属性值，并存储，避免重复获取
    const initMenuList = () => {
        const letterListDom = letterListRef.current;

        if (letterListDom && data.length) {
            // 因外层动画原因，所获取 rect 处于变动过程中的值，需等待外层动画执行完成
            setTimeout(() => {
                const rect = letterListDom.getBoundingClientRect();
                const letterItemHeight = (letterListDom.children[0].clientHeight || 18) + 1;
                const letterRoundHeight = rect.height - (rect.height % letterItemHeight);

                letterPosRef.current = {
                    ...letterPosRef.current,
                    letterItemHeight,
                    letterRoundHeight
                };
            }, 10);
        }
    };

    // 获取列表对应属性，并存储，避免重复获取
    const initCountryList = () => {
        const listDom = sectionRef.current;

        if (listDom) {
            // 因外层动画原因，所获取 rect 处于变动过程中的值，需等待外层动画执行完成
            setTimeout(() => {
                let territory = 0;

                const temp = Array.from(listDom.children).map((child: HTMLElement, index: number) => {
                    // 计算改 item 底部临界线
                    territory += child.clientHeight + parseInt(getComputedStyle(child).marginTop);

                    return { letter: data[index].letter || '', territory };
                });

                countryListMapRef.current = temp;
            }, 10);
        }
    };

    // 定位到current country
    const countryAutoPosition = (value: string) => {
        const listDom = sectionRef.current;
        const optionItemDoms = optionItemRefs.current;

        if (listDom && optionItemDoms.length) {
            const elem = optionItemDoms.find(item => item!.getAttribute('data-code') === value);

            if (elem) {
                // 滚动条高度 = 元素的 offsetTop - 元素高度*2
                listDom.scrollTop = elem.offsetTop - elem.clientHeight * 2;
            }
        }
    };

    // 在导航没有触发 touchMove 时的定位
    const letterAutoPosition = () => {
        const letterListDom = letterListRef.current;

        if (letterListDom) {
            const letterActive = Array.from(letterListDom.children).find(child =>
                child.classList.contains('active')
            ) as HTMLAnchorElement;

            if (letterActive) {
                const top = letterActive.offsetTop;

                letterListDom.scrollTop = top > letterListDom.clientHeight / 3 ? top : 0;
            }
        }
    };

    // 单词点击
    const handleLetterClick = (letter: string) => {
        scrollTo(letter);
    };

    // 菜单滑动选中
    const handleLetterTouchMove = (event?: React.TouchEvent) => {
        if (event) {
            event.stopPropagation();

            const { letterItemHeight, letterRoundHeight, letterRectTop } = letterPosRef.current;

            if (letterListRef.current && letterItemHeight && letterRoundHeight) {
                isTouchMoveRef.current = true;

                const pageY = event.touches[0].pageY;
                const top = letterListRef.current!.getBoundingClientRect().top;
                const tempLetterRectTop = letterRectTop || top;
                const offsetTop = pageY - tempLetterRectTop + letterListRef.current.scrollTop;
                const index = Math.floor(offsetTop / letterItemHeight);

                letterPosRef.current = { ...letterPosRef.current, letterRectTop: top };

                if (index >= 0 && index <= data.length - 1) {
                    const letter = data[index].letter;
                    const maxHeight = letterRoundHeight - letterItemHeight / 2;

                    if (offsetTop - letterListRef.current.scrollTop > maxHeight) {
                        letterListRef.current.scrollTop =
                            Math.ceil((offsetTop - maxHeight) / letterItemHeight) * letterItemHeight;
                    } else if (offsetTop - letterListRef.current.scrollTop <= letterItemHeight) {
                        letterListRef.current.scrollTop -= letterItemHeight;
                    }

                    scrollTo(letter);
                }
            }
        }
    };

    const handleLetterTouchEnd = () => {
        isTouchMoveRef.current = false;
    };

    // 国家列表滚动到对应 letter 位置
    const scrollTo = (letter: string) => {
        const sectionDom = sectionRef.current;

        if (sectionDom) {
            const elem = Array.from(sectionDom.children).find(child => child.id === `countrycode-letter-${letter}`);

            if (elem) {
                elem.scrollIntoView();

                setCurLetter(letter);
            }
        }
    };

    return (
        <>
            <div
                className="letter-lists"
                ref={letterListRef}
                onTouchEnd={handleLetterTouchEnd}
                onTouchMove={handleLetterTouchMove}>
                {data.map(({ letter }) => (
                    <HashLink
                        key={letter}
                        className={classlist('letter-item', {
                            active: curLetter === letter
                        })}
                        onClick={() => handleLetterClick(letter)}>
                        {letter}
                    </HashLink>
                ))}
            </div>
            <section className="select-view" ref={sectionRef}>
                {data.map(({ letter, province: provinceData }) => (
                    <div key={letter} id={`countrycode-letter-${letter}`} className="list-items">
                        <div className="list-letter" onClick={() => handleLetterClick(letter)}>
                            {letter}
                        </div>
                        <>
                            {provinceData.map(({ province }, provIndex) => {
                                const isSelected = value === province;

                                return (
                                    <div
                                        key={province}
                                        ref={optionItem => (optionItemRefs.current[provIndex] = optionItem)}
                                        className={classlist('option-item', {
                                            selected: isSelected
                                        })}
                                        data-code={province}
                                        onClick={() => onChange?.(province)}>
                                        {province}
                                    </div>
                                );
                            })}
                        </>
                    </div>
                ))}
            </section>
        </>
    );
};

export default ViewWithNav;
